/**
 * Spot Management Page - 스팟 관리 (프로토타입)
 * 더미 데이터 사용
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  HiLocationMarker,
  HiPlus,
  HiPencil,
  HiTrash,
  HiLogout,
  HiSparkles,
  HiX,
  HiViewGrid,
  HiMap,
  HiChartBar
} from 'react-icons/hi';
import { mockSpotsData } from '../data/mockData';
import MapContainer from '../components/MapContainer';
import SensorDataGrid from '../components/SensorDataGrid';

export default function SpotManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [spots] = useState(mockSpotsData);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [activeTab, setActiveTab] = useState('spots'); // 'spots' or 'sensorData'

  // URL 쿼리 파라미터에서 탭 상태 읽기
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    if (tab === 'sensor-data') {
      setActiveTab('sensorData');
    } else {
      setActiveTab('spots');
    }
  }, [location.search]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Header */}
      <motion.header
        className="relative overflow-hidden shadow-2xl border-b-2"
        style={{
          background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
          borderColor: 'var(--color-primary)',
        }}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-5">
          <div className="flex justify-between items-center">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl shadow-lg border border-white/30"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <HiSparkles className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <h1 className="text-2xl font-display font-extrabold text-white tracking-tight drop-shadow-lg">
                    팔팔구구 <span className="bg-gradient-to-r from-yellow-200 to-pink-200 bg-clip-text text-transparent">메타보맵</span>
                  </h1>
                  <p className="text-sm font-medium text-white/90 mt-0.5">스팟 관리</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center space-x-4"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="text-right">
                <p className="text-sm font-bold text-white drop-shadow-md">Admin</p>
                <p className="text-xs font-medium text-white/80">관리자</p>
              </div>
              <motion.button
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-xl hover:bg-white/30 flex items-center gap-2 shadow-lg transition-all"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <HiLogout className="w-4 h-4" />
                로그아웃
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-8">
        {/* Navigation Tabs */}
        <motion.div
          className="mb-8 rounded-2xl shadow-lg border-2 p-2"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-primary)',
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <nav className="flex space-x-2">
            <motion.button
              onClick={() => navigate('/dashboard')}
              className="py-3 px-6 rounded-xl font-semibold text-sm relative flex items-center gap-2 transition-all duration-200"
              style={{ color: 'var(--color-text)' }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiViewGrid className="w-5 h-5" />
              대시보드
            </motion.button>
            <motion.button
              onClick={() => {
                navigate('/spots');
                setActiveTab('spots');
              }}
              className={`py-3 px-6 rounded-xl font-semibold text-sm relative flex items-center gap-2 transition-all duration-200 ${
                activeTab === 'spots'
                  ? ''
                  : ''
              }`}
              style={{
                background: activeTab === 'spots' 
                  ? `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`
                  : 'transparent',
                color: activeTab === 'spots' ? 'white' : 'var(--color-text)',
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiMap className="w-5 h-5" />
              스팟 관리
            </motion.button>
            <motion.button
              onClick={() => {
                navigate('/spots?tab=sensor-data');
                setActiveTab('sensorData');
              }}
              className={`py-3 px-6 rounded-xl font-semibold text-sm relative flex items-center gap-2 transition-all duration-200 ${
                activeTab === 'sensorData'
                  ? ''
                  : ''
              }`}
              style={{
                background: activeTab === 'sensorData'
                  ? `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`
                  : 'transparent',
                color: activeTab === 'sensorData' ? 'white' : 'var(--color-text)',
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiChartBar className="w-5 h-5" />
              스팟별 측정자료 조회
            </motion.button>
          </nav>
        </motion.div>

        {activeTab === 'spots' ? (
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
            {/* Left: Spot List - 40% */}
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Spot List */}
              <motion.div
          className="rounded-xl shadow-lg border-2 overflow-hidden"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-primary)',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="px-6 py-5 border-b-2 flex justify-between items-center" style={{ borderColor: 'var(--color-primary)' }}>
            <h3 className="text-xl font-display font-bold" style={{ color: 'var(--color-text)' }}>
              스팟 목록 ({spots.length})
            </h3>
            <motion.button
              onClick={() => setIsFormOpen(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 shadow-lg text-white"
              style={{
                background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiPlus className="w-4 h-4" />
              새 스팟
            </motion.button>
          </div>

          <div className="overflow-y-auto max-h-[600px] p-4">
            <div className="grid grid-cols-1 gap-3">
              {spots.map((spot, index) => (
                <motion.div
                  key={spot.id}
                  className="rounded-lg p-4 cursor-pointer transition-all duration-200 border-2"
                  style={{
                    background: selectedSpot?.id === spot.id 
                      ? `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`
                      : 'var(--color-surface)',
                    borderColor: selectedSpot?.id === spot.id ? 'var(--color-accent)' : 'var(--color-primary)',
                    color: selectedSpot?.id === spot.id ? 'white' : 'var(--color-text)',
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ y: -2, scale: 1.01 }}
                  onClick={() => setSelectedSpot(spot)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-md font-semibold">{spot.spot_name}</h4>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        spot.magnetic_field_strength === 'strong'
                          ? 'bg-red-100 text-red-800'
                          : spot.magnetic_field_strength === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {spot.magnetic_field_strength === 'strong'
                        ? '강함'
                        : spot.magnetic_field_strength === 'medium'
                        ? '보통'
                        : '약함'}
                    </span>
                  </div>
                  <p className="text-sm mb-2 line-clamp-2 opacity-80">{spot.short_description}</p>
                  <div className="flex items-center text-xs opacity-70">
                    <HiLocationMarker className="w-4 h-4 mr-1" />
                    <span>{spot.latitude.toFixed(6)}, {spot.longitude.toFixed(6)}</span>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSpot(spot);
                        setIsFormOpen(true);
                      }}
                      className="p-2 rounded-full transition-colors"
                      style={{
                        background: selectedSpot?.id === spot.id ? 'rgba(255,255,255,0.2)' : 'var(--color-primary)',
                        color: selectedSpot?.id === spot.id ? 'white' : 'white',
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <HiPencil className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('정말로 이 스팟을 삭제하시겠습니까?')) {
                          // 프로토타입에서는 실제 삭제하지 않음
                        }
                      }}
                      className="p-2 rounded-full transition-colors"
                      style={{
                        background: selectedSpot?.id === spot.id ? 'rgba(255,255,255,0.2)' : 'var(--color-secondary)',
                        color: 'white',
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <HiTrash className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
            </motion.div>

            {/* Right: Map - 60% */}
            <motion.div
              className="lg:col-span-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="h-[600px] rounded-xl shadow-lg border-2 overflow-hidden" style={{ borderColor: 'var(--color-primary)' }}>
                <MapContainer
                  spots={spots}
                  selectedSpot={selectedSpot}
                  onSpotClick={setSelectedSpot}
                />
              </div>
            </motion.div>
          </div>
        ) : (
          <SensorDataGrid />
        )}

        {/* Form Modal */}
        {isFormOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setIsFormOpen(false)}
          >
            <motion.div
              className="rounded-xl shadow-2xl w-full max-w-md relative p-8"
              style={{ background: 'var(--color-surface)' }}
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.button
                onClick={() => setIsFormOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-full hover:opacity-70 transition-all"
                style={{ background: 'var(--color-background)', color: 'var(--color-text)' }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <HiX className="w-5 h-5" />
              </motion.button>
              <h2 className="text-2xl font-display font-bold mb-6" style={{ color: 'var(--color-text)' }}>
                {selectedSpot ? '스팟 수정' : '새 스팟 등록'}
              </h2>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                프로토타입 모드: 실제 저장되지 않습니다.
              </p>
            </motion.div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
